import {Component, inject, OnInit, PLATFORM_ID} from '@angular/core';
import {OrderOfferProductsDialogComponent} from '../order-offer-products-dialog/order-offer-products-dialog.component';
import {MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {PromosService} from '../../../services/promos.service';
import {CartItemType, CartService, ProductCartItem} from '../../../services/cart.service';
import {OrderContextStore} from '../../../store/order-context.store';
import {OrderOfferDialogComponent} from '../order-offer-dialog/order-offer-dialog.component';
import {PromoProductOfferDialogComponent} from './promo-product-offer-dialog/promo-product-offer-dialog.component';
import {ProductDiscountDialogComponent} from './product-discount-dialog/product-discount-dialog.component';
import {OrderOfferRulesDialogComponent} from './order-offer-rules-dialog/order-offer-rules-dialog.component';
import {OrderDiscountDialogComponent} from '../order-discount-dialog/order-discount-dialog.component';
import {Router} from '@angular/router';
import {OrderComponent} from '../../../order/order.component';
import {isPlatformBrowser} from '@angular/common';
import {LoginComponent} from '../../../login/login.component';
import {AuthenticationService} from '../../../services/authentication.service';
import {THEME_CONFIG} from '../../../../environments/tokens/theme.token';
import {CdkScrollable} from '@angular/cdk/scrolling';
import {FormsModule} from '@angular/forms';
import {MatButton} from '@angular/material/button';

@Component({
  selector: 'app-promo-code-dialog',
  templateUrl: './promo-code-dialog.component.html',
  styleUrl: './promo-code-dialog.component.scss',
  imports: [CdkScrollable, MatDialogContent, FormsModule, MatButton],
  standalone: true
})
export class PromoCodeDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<PromoCodeDialogComponent>);
  private promosService = inject(PromosService);
  private cartService = inject(CartService);
  private authenticationService = inject(AuthenticationService);
  private dialog = inject(MatDialog);
  private orderContextStore = inject(OrderContextStore);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  promoCode = '';
  isLoading = false;
  errorMessage = '';
  successPromo: any = null;
  data = inject(MAT_DIALOG_DATA);
  protected readonly themeConfig = inject(THEME_CONFIG)

  private openNextDialog<T>(component: any, config: any): MatDialogRef<T> {
    // this.dialogRef.close();
    return this.dialog.open<T>(component, config);
  }

  ngOnInit(): void {
    if (this.data?.promoCode) {
      this.promoCode = this.data.promoCode;
      this.search();
    }
  }

  getAppliedPromoCode(): string | null {
    const promoItem = this.cartService.promotionItems()
      .find(item => item.promotion?.addedByPromoCode === true);
    if (promoItem) return promoItem.promotion?.promoCode || null;

    const discountedProduct = this.cartService.productItems()
      .find(item => (item as any).addedByPromoCode === true);
    if (discountedProduct) return (discountedProduct as any).promoCode || null;

    const orderDiscount = this.cartService.orderDiscount();
    if (orderDiscount?.promotion?.addedByPromoCode === true)
      return orderDiscount.promotion?.promoCode || null;

    return null;
  }

  search(): void {
    if (!this.promoCode.trim()) return;

    if (this.hasPromoCodeAlreadyApplied()) {
      const appliedCode = this.getAppliedPromoCode();

      if (appliedCode && appliedCode.toLowerCase() === this.promoCode.trim().toLowerCase()) {
        this.errorMessage = 'SAME_CODE';
      } else {
        this.errorMessage = 'OTHER_CODE';
      }
      this.successPromo = null;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successPromo = null;

    this.promosService.getPromotionWithCode(this.promoCode.trim()).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        const promo = response?.body?.data;
        if (!promo) {
          this.errorMessage = 'Code promo introuvable.';
          return;
        }
        this.successPromo = promo;
        this.apply()
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Code promo invalide ou expiré.';
      }
    });
  }

  private isOrderContextMissing(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    try {
      const stored = localStorage.getItem('order_context_type');
      if (!stored) return true;

      const context = JSON.parse(stored);
      return !context?.orderType || context.orderType === null;
    } catch (error) {
      console.error('Erreur lors de la vérification du order_context_type:', error);
      return true;
    }
  }

  private openOrderDialogForPromo(): void {
    this.dialogRef.close();

    const orderDialogRef = this.dialog.open(OrderComponent, {
      panelClass: 'custom-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        stayOnPromoDialog: true
      }
    });


    orderDialogRef.afterClosed().subscribe((result: any) => {
      if (result === true || this.orderContextStore.orderType() !== null) {
        setTimeout(() => {
          this.apply();
        }, 300);
      }
    });
  }

  apply(): void {
    if (!this.successPromo) return;
    if (this.hasPromoCodeAlreadyApplied()) {
      this.errorMessage = 'Un code promo est déjà appliqué. Retirez-le avant d\'en utiliser un autre.';
      this.successPromo = null;
      return; // ← stop, pas d'application
    }
    if (this.isOrderContextMissing()) {
      this.openOrderDialogForPromo();
      return;
    }
    const promo = this.successPromo;
    const type = promo.type;

    // if (type === 'product-offer') {
    //   this.openNextDialog(PromoProductOfferDialogComponent, {
    //     panelClass: 'custom-dialog',
    //     width: '500px',
    //     maxWidth: '95vw',
    //     maxHeight: '90vh',
    //     disableClose: false,
    //     autoFocus: false,
    //     data: {promotion: promo}
    //   });
    //   return;
    // }
    if (type === 'product-offer') {
      const promoOfferRef = this.openNextDialog<PromoProductOfferDialogComponent>(PromoProductOfferDialogComponent, {
        panelClass: 'custom-dialog',
        width: '500px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: false,
        autoFocus: false,
        data: {promotion: promo, promoCode: this.promoCode}
      });

      promoOfferRef.afterClosed().subscribe((result: any) => {
        if (result === 'added') {
          this.dialogRef.close();
        } else {
          this.dialogRef.close();
        }
      });
      return;
    }
    if (type === 'product-discount') {
      // this.dialogRef.close();
      const rules: any[] = promo.payload?.rules || [];
      if (rules.length === 0) return;

      const syntheticRule = {
        reduction: promo.payload?.reduction,
        categories: rules.map((r: any) => ({
          category: r.category,
          quantity: r.quantity || 1,
          sizes: r.sizes || [],
          products: r.products || []
        }))
      };

      const dialogRef = this.dialog.open(ProductDiscountDialogComponent, {
        panelClass: 'custom-dialog',
        width: '500px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        data: {
          promotion: {...promo, promoCode: this.promoCode}, // ✅ PASSER promoCode
          rule: syntheticRule
        }
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result?.action === 'confirm' && result.selectedProducts) {
          this.addProductPromoToCart(promo, syntheticRule, result.selectedProducts);
        }
        this.dialogRef.close()
      });
      return;
    }

    if (type === 'order-offer') {
      const rules: any[] = promo.payload?.rules || [];
      if (rules.length === 0) {
        this.errorMessage = 'Cette promotion ne contient aucun intervalle.';
        return;
      }

      const selections: any[] = promo.payload?.selections || [];
      const matchingCartItems = this.cartService.items()
        .filter(item => item.type === CartItemType.PRODUCT)
        .filter(item => {
          const productItem = item as ProductCartItem;
          return selections.some((sel: any) => {
            const productMatch = (sel.products || []).some(
              (p: any) => p.id === productItem.product.id
            );
            if (!productMatch) return false;
            const selSizes: any[] = sel.sizes || [];
            if (selSizes.length === 0) return true;
            const cartSizeObj = productItem.product.pricePerSizes?.find(
              (pps: any) => pps.size.id === productItem.selectedSize
            );
            const cartSizeName = cartSizeObj?.size?.name || '';
            return selSizes.some((s: any) =>
              s.id === productItem.selectedSize || s.name === cartSizeName
            );
          });
        }) as ProductCartItem[];

      const promoEligibleTotal = matchingCartItems.reduce((sum, item) => sum + item.total, 0);

      // ✅ Vérifier si un seul intervalle est éligible → skip le dialog
      const eligibleRules = rules.filter((r: any) =>
        promoEligibleTotal >= r.min && promoEligibleTotal <= r.max
      );

      if (eligibleRules.length === 1) {
        // ✅ Aller directement au dialog suivant sans ouvrir OrderOfferRulesDialogComponent
        this.openOfferDialog(promo, rules, selections, eligibleRules[0], promoEligibleTotal);
        return;
      }

      // Sinon ouvrir le dialog des règles normalement
      const rulesDialogRef = this.dialog.open(OrderOfferRulesDialogComponent, {
        panelClass: 'custom-dialog',
        width: '500px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        data: {promotion: promo, rules: rules, promoEligibleTotal: promoEligibleTotal}
      });

      rulesDialogRef.afterClosed().subscribe((rulesResult: any) => {
        if (!rulesResult || rulesResult.action === 'cancel') {
          this.dialogRef.close();
          return;
        }
        if (rulesResult.action === 'confirm') {
          this.openOfferDialog(promo, rules, selections, rulesResult.rule, promoEligibleTotal);
        }
      });
      return;
    }

    if (type == 'order-discount') {
      if (this.cartService.isEmpty()) {
        this.errorMessage = 'Votre panier est vide. Veuillez ajouter des articles avant d\'appliquer ce code promo.';
        this.successPromo = null;
        return;
      }
      const rules: any[] = promo.payload?.rules || [];
      if (rules.length === 0) {
        this.errorMessage = 'Cette promotion ne contient aucun intervalle.';
        return;
      }

      const selections: any[] = promo.payload?.selections || [];
      const matchingCartItems = this.cartService.items()
        .filter(item => item.type === CartItemType.PRODUCT)
        .filter(item => {
          const productItem = item as ProductCartItem;
          return selections.some((sel: any) => {
            // Vérifier le produit
            const productMatch = (sel.products || []).some(
              (p: any) => p.id === productItem.product.id
            );
            if (!productMatch) return false;

            // Vérifier la taille si elle est définie
            const selSizes: any[] = sel.sizes || [];
            if (selSizes.length === 0) return true;

            // Chercher le nom de la taille sélectionnée dans le panier
            const cartSizeObj = productItem.product.pricePerSizes?.find(
              (pps: any) => pps.size.id === productItem.selectedSize
            );
            const cartSizeName = cartSizeObj?.size?.name || '';

            return selSizes.some((s: any) =>
              s.id === productItem.selectedSize || s.name === cartSizeName
            );
          });
        }) as ProductCartItem[];

      const promoEligibleTotal = matchingCartItems.reduce((sum, item) => sum + item.total, 0);

      // this.dialogRef.close();

      const rulesDialogRef = this.dialog.open(OrderOfferRulesDialogComponent, {
        panelClass: 'custom-dialog',
        width: '500px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        data: {
          promotion: promo,
          rules: rules,
          promoEligibleTotal: promoEligibleTotal
        }
      });

      rulesDialogRef.afterClosed().subscribe((rulesResult: any) => {
        if (!rulesResult || rulesResult.action === 'cancel') {
          this.dialogRef.close();
          return;
        }

        if (rulesResult.action === 'confirm') {
          const matchedRule = rulesResult.rule;

          const freshTotal = this.cartService.items()
            .filter(item => item.type === CartItemType.PRODUCT)
            .filter(item => {
              const productItem = item as ProductCartItem;
              return selections.some((sel: any) => {
                const productMatch = (sel.products || []).some(
                  (p: any) => p.id === productItem.product.id
                );
                if (!productMatch) return false;
                const selSizes: any[] = sel.sizes || [];
                if (selSizes.length === 0) return true;
                const cartSizeObj = productItem.product.pricePerSizes?.find(
                  (pps: any) => pps.size.id === productItem.selectedSize
                );
                const cartSizeName = cartSizeObj?.size?.name || '';
                return selSizes.some((s: any) =>
                  s.id === productItem.selectedSize || s.name === cartSizeName
                );
              });
            })
            .reduce((sum, item) => sum + (item as ProductCartItem).total, 0);

          if (freshTotal < matchedRule.min || freshTotal > matchedRule.max) {
            this.dialog.open(OrderOfferRulesDialogComponent, {
              panelClass: 'custom-dialog',
              width: '500px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              disableClose: true,
              data: {promotion: promo, rules: rules, promoEligibleTotal: freshTotal}
            });
            return;
          }


          // this.dialogRef.close();
          const offerDialogRef = this.dialog.open(OrderDiscountDialogComponent, {
            panelClass: 'custom-dialog',
            width: '500px',
            disableClose: true,
            data: {
              promotion: promo, rule: matchedRule, promoEligibleTotal: freshTotal,
              cartTotal: freshTotal
            }

          });
          offerDialogRef.afterClosed().subscribe(() => {
            this.dialogRef.close('discount-applied');

            if (!this.authenticationService.isLogin()) {
              const dialogRef = this.dialog.open(LoginComponent, {panelClass: 'custom-dialog'});
              dialogRef.afterClosed().subscribe((result) => {
                if (result === true) {
                  this.router.navigate(['/command']);

                }
              });
              return;
            }
            this.cartService.setOrderDiscount({
              promotion: {...promo, addedByPromoCode: true, promoCode: this.promoCode},
              rule: matchedRule
            });
            if (!this.router.url.includes('/command')) {
              this.router.navigate(['/command']);
            }
          });
        }
      })
    }

  }

  private openOfferDialog(promo: any, rules: any[], selections: any[], matchedRule: any, promoEligibleTotal: number): void {
    const freshTotal = this.cartService.items()
      .filter(item => item.type === CartItemType.PRODUCT)
      .filter(item => {
        const productItem = item as ProductCartItem;
        return selections.some((sel: any) => {
          const productMatch = (sel.products || []).some(
            (p: any) => p.id === productItem.product.id
          );
          if (!productMatch) return false;
          const selSizes: any[] = sel.sizes || [];
          if (selSizes.length === 0) return true;
          const cartSizeObj = productItem.product.pricePerSizes?.find(
            (pps: any) => pps.size.id === productItem.selectedSize
          );
          const cartSizeName = cartSizeObj?.size?.name || '';
          return selSizes.some((s: any) =>
            s.id === productItem.selectedSize || s.name === cartSizeName
          );
        });
      })
      .reduce((sum, item) => sum + (item as ProductCartItem).total, 0);

    if (freshTotal < matchedRule.min || freshTotal > matchedRule.max) {
      this.dialog.open(OrderOfferRulesDialogComponent, {
        panelClass: 'custom-dialog',
        width: '500px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        data: {promotion: promo, rules: rules, promoEligibleTotal: freshTotal}
      });
      return;
    }

    const offerDialogRef = this.dialog.open(OrderOfferDialogComponent, {
      panelClass: 'custom-dialog',
      width: '500px',
      disableClose: true,
      data: {promotion: promo, rule: matchedRule, promoEligibleTotal: freshTotal}
    });

    offerDialogRef.afterClosed().subscribe((result: any) => {
      if (!result || result.action === 'ignore') {
        this.dialogRef.close();
        return;
      }

      if (result.action === 'customize') {
        const productsDialogRef = this.dialog.open(OrderOfferProductsDialogComponent, {
          panelClass: 'custom-dialog',
          width: '500px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          disableClose: true,
          data: {promotion: promo, rule: matchedRule}
        });

        productsDialogRef.afterClosed().subscribe((prodResult: any) => {
          if (prodResult?.action === 'confirm' && prodResult.selectedProducts) {
            this.addOrderOfferToCart(promo, matchedRule, prodResult.selectedProducts);
          }
          this.dialogRef.close();
        });
      }
    });
  }

  private parseProductPrice(product: any, size?: any): number {
    const price = product?.price;
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      return parseFloat(price.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    }
    if (typeof price === 'object' && price !== null) {
      if (size?.name && price[size.name]) {
        return parseFloat(price[size.name].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
      }
      const firstKey = Object.keys(price)[0];
      if (firstKey && price[firstKey]) {
        return parseFloat(price[firstKey].replace(/[^\d,]/g, '').replace(',', '.')) || 0;
      }
    }
    return product?.price?.amount || 0;
  }

  private addOrderOfferToCart(promotion: any, rule: any, selectedProducts: any[]): void {
    const reduction = rule.reduction || promotion.payload?.reduction;

    let offeredItems: any[];
    let cartTotal: number;

    if (reduction?.type === 'FIXED_AMOUNT') {
      // ✅ FIX: utilise parseProductPrice au lieu de price?.amount
      const totalBase = selectedProducts.reduce((sum, sel) =>
        sum + (this.parseProductPrice(sel.product, sel.size) * (sel.quantity || 1)), 0);

      const fixedDiscount = reduction.value || 0;
      const totalAfterDiscount = Math.max(0, totalBase - fixedDiscount);
      const discountRatio = totalBase > 0 ? totalAfterDiscount / totalBase : 0;

      offeredItems = selectedProducts.map(sel => {
        // ✅ FIX
        const basePrice: number = this.parseProductPrice(sel.product, sel.size);
        const config = sel.configuration || null;
        const supplements: any[] = config?.selectedSupplements || [];
        const suppTotal = supplements.reduce((sum: number, sup: any) =>
          sum + ((sup.price || 0) * (sup.quantity || 1)), 0);
        const discountedUnitPrice = Math.floor(basePrice * discountRatio * 100) / 100;
        const finalPrice = Math.floor((discountedUnitPrice + suppTotal) * 100) / 100;
        return {
          product: sel.product,
          quantity: sel.quantity,
          rule: {...rule, sizes: sel.size ? [sel.size] : []},
          unitBasePrice: basePrice,
          unitSupplementsPrice: suppTotal,
          unitPrice: finalPrice,
          totalBasePrice: basePrice * sel.quantity,
          totalSupplementsPrice: suppTotal * sel.quantity,
          totalPrice: finalPrice * sel.quantity,
          excludedIngredients: config?.removedIngredients || [],
          selectedAdditions: config?.selectedAccompaniments || [],
          selectedSupplements: config?.selectedSupplements || [],
        };
      });

      const totalSupplements = offeredItems.reduce((sum, item) =>
        sum + (item.unitSupplementsPrice || 0) * (item.quantity || 1), 0);
      cartTotal = Math.floor((totalAfterDiscount + totalSupplements) * 100) / 100;

    } else {
      offeredItems = selectedProducts.map(sel => {
        // ✅ FIX
        const basePrice: number = this.parseProductPrice(sel.product, sel.size);
        const discountedPrice = reduction
          ? Math.round(basePrice * (1 - (reduction.value || 0)) * 100) / 100
          : 0;
        const config = sel.configuration || null;
        const supplements: any[] = config?.selectedSupplements || [];
        const suppTotal = supplements.reduce((sum: number, sup: any) =>
          sum + ((sup.price || 0) * (sup.quantity || 1)), 0);
        const finalPrice = discountedPrice + suppTotal;
        return {
          product: sel.product,
          quantity: sel.quantity,
          rule: {...rule, sizes: sel.size ? [sel.size] : []},
          unitBasePrice: basePrice,
          unitSupplementsPrice: suppTotal,
          unitPrice: finalPrice,
          totalBasePrice: basePrice * sel.quantity,
          totalSupplementsPrice: suppTotal * sel.quantity,
          totalPrice: finalPrice * sel.quantity,
          excludedIngredients: config?.removedIngredients || [],
          selectedAdditions: config?.selectedAccompaniments || [],
          selectedSupplements: config?.selectedSupplements || [],
        };
      });
      cartTotal = offeredItems.reduce((sum, item) => sum + item.totalPrice, 0);
    }

    const promotionWithReduction = {
      ...promotion,
      addedByPromoCode: true,
      promoCode: this.promoCode,
      payload: {
        ...promotion.payload,
        reduction: promotion.payload?.reduction || rule.reduction,
        appliedRule: {min: rule.min, max: rule.max}
      }
    };

    this.cartService.addPromotion({
      promotion: promotionWithReduction,
      promotionImage: promotion.image || '',
      buyItems: [],
      offeredItems,
      customizedItems: {},
      total: cartTotal
    });
  }

  private addProductPromoToCart(promo: any, rule: any, selectedProducts: any[]): void {
    const reduction = rule.reduction || promo.payload?.reduction;
    const offeredItems = selectedProducts.map(sel => {
      const basePrice: number = sel.product?.price?.amount || 0;
      const discountedPrice = reduction?.type === 'PERCENTAGE'
        ? Math.round(basePrice * (1 - (reduction.value || 0)) * 100) / 100
        : 0;
      const config = sel.configuration || null;
      const supplements: any[] = config?.selectedSupplements || [];
      const suppTotal = supplements.reduce((sum: number, sup: any) =>
        sum + ((sup.price || 0) * (sup.quantity || 1)), 0);
      const finalPrice = discountedPrice + suppTotal;
      return {
        product: sel.product,
        quantity: sel.quantity || 1,
        rule: {...rule, sizes: sel.size ? [sel.size] : []},
        unitBasePrice: basePrice,
        unitSupplementsPrice: suppTotal,
        unitPrice: finalPrice,
        totalBasePrice: basePrice * (sel.quantity || 1),
        totalSupplementsPrice: suppTotal * (sel.quantity || 1),
        totalPrice: finalPrice * (sel.quantity || 1),
        excludedIngredients: config?.removedIngredients || [],
        selectedAdditions: config?.selectedAccompaniments || [],
        selectedSupplements: config?.selectedSupplements || [],
      };
    });

    const cartTotal = offeredItems.reduce((sum, item) => sum + item.totalPrice, 0);
    this.cartService.addPromotion({
      promotion: {
        ...promo, addedByPromoCode: true,
        promoCode: this.promoCode,
        payload: {...promo.payload, reduction}
      },
      promotionImage: promo.image || '',
      buyItems: [],
      offeredItems,
      customizedItems: {},
      total: cartTotal
    });
  }

  private hasPromoCodeAlreadyApplied(): boolean {
    // Vérifie les promotions (product-offer, order-offer)
    const hasPromoItem = this.cartService.promotionItems().some(
      (item) => item.promotion?.addedByPromoCode === true
    );

    // Vérifie les produits avec remise par code promo (product-discount)
    const hasDiscountedProduct = this.cartService.productItems().some(
      (item) => item.addedByPromoCode === true
    );

    // Vérifie la remise commande (order-discount)
    const hasOrderDiscount =
      this.cartService.orderDiscount()?.promotion?.addedByPromoCode === true;

    return hasPromoItem || hasDiscountedProduct || hasOrderDiscount;
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
